import { useCallback, useRef, useState } from 'react';

import SupplierService from 'src/modules/3rd-party-management/apis/common/SupplierService';

export const VAT_CHECK_STATUS = {
    IDLE        :   'idle',
    CHECKING    :   'checking',
    VALID       :   'valid',
    INVALID     :   'invalid',
    UNAVAILABLE :   'unavailable'
};

const EMPTY_RESULT = {
    status  :   VAT_CHECK_STATUS.IDLE,
    details :   null
};

/**
 * Verifies a VAT ID against the EU VIES registry.
 *
 * The outcome is advisory only: a failed check never prevents the third party
 * from being created or updated, it just surfaces a warning to the admin.
 */
const useVatNumberCheck = () => {
    const [ result, setResult ] = useState(EMPTY_RESULT);

    // Guards against a slow response overwriting the result of a newer check.
    const latestCheckRef = useRef(0);
    const lastCheckedValueRef = useRef(null);

    const reset = useCallback(() => {
        latestCheckRef.current += 1;
        lastCheckedValueRef.current = null;

        setResult(EMPTY_RESULT);
    }, []);

    const checkVatNumber = useCallback(async (vatNumber) => {
        const normalizedVatNumber = (vatNumber || '').trim();

        if(!normalizedVatNumber){
            reset();

            return;
        }

        if(lastCheckedValueRef.current === normalizedVatNumber){
            return;
        }

        const checkId = latestCheckRef.current + 1;

        latestCheckRef.current = checkId;
        lastCheckedValueRef.current = normalizedVatNumber;

        setResult({
            status  :   VAT_CHECK_STATUS.CHECKING,
            details :   null
        });

        try{
            const service = SupplierService.getInstance();
            const response = await service.checkVatNumber(normalizedVatNumber);

            if(latestCheckRef.current !== checkId) return;

            const details = response?.data || {};

            if(response?.ok === false || details.error){
                setResult({
                    status  :   VAT_CHECK_STATUS.UNAVAILABLE,
                    details
                });

                // The value was never really checked, so allow a retry later on.
                lastCheckedValueRef.current = null;

                return;
            }

            setResult({
                status  :   details.valid ? VAT_CHECK_STATUS.VALID : VAT_CHECK_STATUS.INVALID,
                details
            });
        }
        catch(error){
            if(latestCheckRef.current !== checkId) return;

            if(process.env.NODE_ENV === 'development') console.error(error);

            setResult({
                status  :   VAT_CHECK_STATUS.UNAVAILABLE,
                details :   null
            });

            lastCheckedValueRef.current = null;
        }
    }, [ reset ]);

    return {
        status  :   result.status,
        details :   result.details,
        checkVatNumber,
        reset
    };
};

export default useVatNumberCheck;
